import { useEffect } from 'react'
import { useFeaturedMeals } from './hooks/useFeaturedMeals'
import { useReactivation } from './hooks/useReactivation'
import {
  useResurrectionCampaign,
  ResurrectionCampaign
} from './hooks/useResurrectionCampaign'
import { Redirect, useParams, useHistory } from 'react-router-dom'
import './ResurrectionPage.scss'
import { pathOr } from 'ramda'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import { setCookie, getCookie } from '../../utils/browserCookies'
import { RESURRECTION_PATH_COOKIE_NAME } from '../../constants/application'
import Loader from '../../common/Loader'
import Section1 from '../../assets/img/resurrectionSection1.png'
import Section2 from '../../assets/img/resurrectionSection2.png'
import Section3 from '../../assets/img/resurrectionSection3.png'
import Modal from '../../components/NewModal'
import { useFlagsStatus } from '@unleash/proxy-client-react'
import { trackResurrectionLandingPage } from 'src/utils/tracking'
import { MealCardSlider } from 'src/components/MealCardSlider/MealCardSlider'
import { RegularMealCard } from 'src/componentsV2/MenuMealCard/RegularMealCard/RegularMealCard'
import {
  Footer,
  Header,
  Info,
  DishBannerResurrection
} from '../../components/ResurrectionLandingComponents'
import { useLandingPageSections } from '../../hooks/useLandingPageSections'
import HeroSection from '../../componentsV2/newLandingPages/HeroSection'
import { useResurrectionController } from '../../modules/ResurrectionLanding/hooks/useResurrectionController'

const MOCK_HERO_DATA = {
  heroTitle: "We've been cooking up <span>something new</span> for you",
  heroSubtitle:
    "It's been a while since we've cooked for you and there are more reasons than ever to enjoy CookUnity. Let us bring more flavor, more variety, and more restaurant-quality meals to your table.",
  heroPromoCopy: '50% off your first four weeks back',
  ctaLabel: 'Resubscribe now',
  heroImage: 'https://images.ctfassets.net/mock-hero-image.jpg',
  browserTitle: "We've been cooking up something new for you | CookUnity"
}

interface Meal {
  id: string | number
  imageUrl?: string
  image_url?: string
  name?: string
  description?: string
  chef?: {
    name?: string
    id?: string | number
  }
}

interface FeaturedMeals {
  data: Meal[]
}

interface SubscriptionInfo {
  status: string
  currentCredit?: number
}

interface Actions {
  reactivateSubscriptionInit: (params: any) => void
  getResurrectionCampaign: (params: { campaignSlug: string }) => void
  getFeaturedMeals: (params: {
    attrs: { mealIds: number[] }
    source: string
  }) => void
}

interface ResurrectionPageProps {
  actions: Actions
  subscriptionInfo?: SubscriptionInfo
  isReactivating: boolean
  authed: boolean
  location: {
    pathname: string
    search: string
  }
  text: string
  conditionsText: string
  browserTitle?: string
  coupon?: string
  prepaidOfferId?: string
  heroTitle?: string
  heroSubtitle?: string
  resurrectionCampaign?: ResurrectionCampaign
  isResurrectionCampaignLoading: boolean
  featuredMeals?: FeaturedMeals
}

interface RouteParams {
  campaignSlug?: string
}

const ResurrectionPage: React.FC<ResurrectionPageProps> = ({
  actions,
  subscriptionInfo,
  isReactivating,
  authed,
  location,
  text,
  conditionsText,
  browserTitle,
  coupon,
  prepaidOfferId,
  heroTitle,
  heroSubtitle,
  resurrectionCampaign,
  isResurrectionCampaignLoading,
  featuredMeals
}) => {
  const { campaignSlug } = useParams<RouteParams>()
  const { flagsReady } = useFlagsStatus()

  const { enabled: newLandingFlagEnabled, sections } = useLandingPageSections()
  // Fetch resurrection campaign when slug is available
  const {
    result: { contentfulData },
    isLoading
  } = useResurrectionCampaign(
    campaignSlug,
    actions,
    isResurrectionCampaignLoading
  )
  // Enable new landing pages only if both the experiment flag AND the Contentful entry opt-in are true
  const isNewLandingEnabled =
    newLandingFlagEnabled && Boolean(contentfulData?.newDesign)

  // Use the controller hook to handle all the duplicated logic
  const {
    restartAction,
    toggleExpiredModal,
    openExpiredModal,
    shouldShowLoader,
    shouldRedirectToValidateCC,
    urlParams
  } = useResurrectionController({
    location,
    subscriptionInfo,
    isReactivating,
    authed,
    actions,
    resurrectionCampaign: contentfulData || resurrectionCampaign,
    isResurrectionCampaignLoading: isResurrectionCampaignLoading || isLoading,
    flagsReady,
    coupon: contentfulData?.couponCode || coupon,
    prepaidOfferId
  })

  // Fetch featured meals when campaign data is available
  useFeaturedMeals(resurrectionCampaign, urlParams.isFromLogin, actions)

  // Handle subscription reactivation
  useReactivation({
    readyToRestart: urlParams.isFromLogin,
    setReadyToRestart: () => {}, // This is handled by the controller now
    subscriptionInfo,
    isResurrectionCampaignLoading,
    flagsReady,
    resurrectionCampaign: contentfulData || resurrectionCampaign,
    urlParams: {
      chefBrandIds: urlParams.chefBrandIds,
      mealIds: urlParams.mealIds
    },
    actions,
    history: useHistory(),
    coupon: contentfulData?.couponCode || coupon,
    prepaidOfferId,
    pathname: location.pathname
  })

  // Track page load and set cookie
  useEffect(() => {
    trackResurrectionLandingPage(
      'Page Loaded',
      'Resurrection Landing Page Started'
    )

    if (!getCookie(RESURRECTION_PATH_COOKIE_NAME)) {
      setCookie(RESURRECTION_PATH_COOKIE_NAME, location.pathname, 1)
    }
  }, [location.pathname])

  if (shouldRedirectToValidateCC) {
    return <Redirect to="/validate-cc" />
  }

  if (shouldShowLoader || isLoading) {
    return <Loader />
  }

  return (
    <div className="resurrectionContainer">
      {urlParams.isExpired && (
        <Modal isOpen={openExpiredModal} onRequestClose={toggleExpiredModal}>
          <div className="expiredModal">
            <p>
              <h2>Sorry, this offer has ended</h2>
              If you would like to restart your orders contact your account
              executive at{' '}
              <a href="mailto:support@cookunity.com">
                support@cookunity.com
              </a>{' '}
              current promotions.
            </p>
          </div>
        </Modal>
      )}

      <Header restartAction={restartAction} />

      <div
        className={`contentContainer ${
          isNewLandingEnabled ? 'newLanding' : ''
        }`}>
        {isNewLandingEnabled && sections.hero ? (
          <HeroSection
            data={{
              heroTitle: contentfulData?.heroTitle || MOCK_HERO_DATA.heroTitle,
              heroSubtitle:
                contentfulData?.heroSubtitle || MOCK_HERO_DATA.heroSubtitle,
              heroPromoCopy:
                contentfulData?.heroText || MOCK_HERO_DATA.heroPromoCopy,
              ctaLabel: contentfulData?.ctaLabel || MOCK_HERO_DATA.ctaLabel,
              heroImage:
                contentfulData?.heroImageUrl || MOCK_HERO_DATA.heroImage,
              heroImageMobile: contentfulData?.heroImageMobileUrl,
              browserTitle:
                contentfulData?.browserTitle || MOCK_HERO_DATA.browserTitle
            }}
          />
        ) : (
          <DishBannerResurrection
            restartAction={restartAction}
            text={
              pathOr(null, ['heroText'], contentfulData) ||
              pathOr(null, ['heroPromotionCopy'], resurrectionCampaign) ||
              text
            }
            browserTitle={
              pathOr(
                null,
                ['browserTitle'],
                contentfulData || resurrectionCampaign
              ) || browserTitle
            }
            heroTitle={
              pathOr(
                null,
                ['heroTitle'],
                contentfulData || resurrectionCampaign
              ) || heroTitle
            }
            heroSubtitle={
              pathOr(
                null,
                ['heroSubtitle'],
                contentfulData || resurrectionCampaign
              ) || heroSubtitle
            }
            heroImageUrl={
              contentfulData?.heroImageUrl ?? resurrectionCampaign?.heroImageUrl
            }
          />
        )}

        {featuredMeals && (
          <div className="mealsSliderContainer">
            <h2>
              {pathOr(
                null,
                ['menuSectionTitle'],
                contentfulData || resurrectionCampaign
              ) || 'Recommended Meals'}
            </h2>
            <MealCardSlider>
              {Array.isArray(featuredMeals.data) &&
              featuredMeals.data.length > 0
                ? featuredMeals.data.map((meal, i) => (
                    <RegularMealCard
                      key={`${meal.id}-${i}`}
                      meal={{
                        ...meal,
                        imageUrl: meal.imageUrl || meal.image_url
                      }}
                      isEditable={false}
                      onMealClick={() => {}}
                      onChefClick={() => {}}
                    />
                  ))
                : ['', '', '', '', ''].map((_, i) => (
                    <RegularMealCard key={i} meal={{}} isLoading={true} />
                  ))}
            </MealCardSlider>
          </div>
        )}

        <div
          className={`infoContainer ${
            isNewLandingEnabled ? 'newLanding' : ''
          }`}>
          <Info
            title="New chefs. Local kitchens."
            text="We're a collective of over 80 independent chefs and counting, now cooking in New York, LA, Atlanta, Austin, Chicago, Seattle and Miami."
            image={Section1}
            reverse={true}
            isNewLandingEnabled={isNewLandingEnabled}
          />

          <Info
            reverse={false}
            title="Exceptional meals."
            text="We've added more meals, more plant-based alternatives, and more
          premium ingredients to our menu. From truffle mac and cheese to
          charred octopus, you'll enjoy an incredible variety of global
          dishes you won't find anywhere else."
            image={Section2}
            isNewLandingEnabled={isNewLandingEnabled}
          />

          <Info
            title="Biodegradable packaging."
            text="We use industry-leading compostable trays and 100% recyclable
          outer sleeves. We also redesigned our packaging to keep meals
          fresher and increase shelf-life for up to 4-6 days."
            image={Section3}
            reverse={true}
            isNewLandingEnabled={isNewLandingEnabled}
          />
        </div>

        <Footer
          restartAction={restartAction}
          text={
            pathOr(
              null,
              ['heroPromotionCopy'],
              contentfulData || resurrectionCampaign
            ) || text
          }
          conditionsText={
            pathOr(
              null,
              ['conditionsCopy'],
              contentfulData || resurrectionCampaign
            ) || conditionsText
          }
          isNewLandingEnabled={isNewLandingEnabled}
        />
      </div>
    </div>
  )
}

export default ResurrectionPage
